import React from "react";
import { Link } from "react-router-dom";
import { Flame, Sparkles, Scroll, Dices, Swords, BookOpen, Map, Crown } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { useSeoTitle } from "@/lib/useSeoTitle";

const HERO_BG_JPEG = "https://images.pexels.com/photos/28210551/pexels-photo-28210551.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop";
const HERO_BG_WEBP = "https://images.pexels.com/photos/28210551/pexels-photo-28210551.jpeg?auto=compress&cs=tinysrgb&fit=crop&fm=webp";
const HERO_BG_AVIF = "https://images.pexels.com/photos/28210551/pexels-photo-28210551.jpeg?auto=compress&cs=tinysrgb&fit=crop&fm=avif";
const HERO_BG_AVIF_SRCSET = `${HERO_BG_AVIF}&w=768&h=1024 768w, ${HERO_BG_AVIF}&w=1200&h=1600 1200w, ${HERO_BG_AVIF}&w=1920&h=1080 1920w`;
const HERO_BG_WEBP_SRCSET = `${HERO_BG_WEBP}&w=768&h=1024 768w, ${HERO_BG_WEBP}&w=1200&h=1600 1200w, ${HERO_BG_WEBP}&w=1920&h=1080 1920w`;
const HERO_BG_JPEG_SRCSET = `${HERO_BG_JPEG}&w=768&h=1024 768w, ${HERO_BG_JPEG}&w=1200&h=1600 1200w, ${HERO_BG_JPEG}&w=1920&h=1080 1920w`;
const PARCHMENT = "https://images.pexels.com/photos/235985/pexels-photo-235985.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const FEATURES = [
  { icon: BookOpen, title: "Become the Main Character", body: "You aren't a bystander. Every chapter is written around your choices, your character, and the way you speak. The story bends toward you." },
  { icon: Flame, title: "Living AI Narrators", body: "Switchable AI storytellers conjure vivid scenes, characters who react to what you did, and consequences that ripple through your saga." },
  { icon: Scroll, title: "Forge a Hero", body: "Choose who you become — race, class, backstory. Your skills, equipment, inventory and titles carry across every chapter." },
  { icon: Map, title: "Illustrated Story Worlds", body: "High-fantasy realms, gothic moors, steampunk citadels, cyberpunk alleys — each beat auto-illustrated for you." },
  { icon: Sparkles, title: "Memory Codex", body: "A running record of important characters, events, inventory changes, objectives and unresolved threads — fed back to the narrator to improve continuity in long stories." },
  { icon: Dices, title: "Light RPG Elements", body: "Occasional dice rolls, skills, equipment, inventory and objectives add flavour and structure. No complicated rules required." },
];

export default function Landing() {
  const { user } = useAuth();
  useSeoTitle({
    title: "AI Novel – Become the Main Character | TalesTalesTales",
    description: "Play an AI Novel where your choices shape the story. Explore illustrated worlds with persistent memory and light RPG elements.",
    path: "/",
  });
  const beginHref = user ? "/dashboard" : "/trial";
  const forgeHref = user ? "/character/new" : "/trial";
  return (
    <div data-testid="landing-page" className="min-h-screen bg-[#0f0d11]">
      <Nav />
      <main id="main-content">

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <picture>
            <source type="image/avif" srcSet={HERO_BG_AVIF_SRCSET} sizes="100vw" />
            <source type="image/webp" srcSet={HERO_BG_WEBP_SRCSET} sizes="100vw" />
            <img
              src={HERO_BG_JPEG}
              srcSet={HERO_BG_JPEG_SRCSET}
              sizes="100vw"
              alt=""
              width="1920"
              height="1080"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="w-full h-full object-cover opacity-40"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-b from-[#0f0d11]/40 via-[#0f0d11]/70 to-[#0f0d11]" />
          <div className="absolute inset-0" style={{
            background: "radial-gradient(ellipse at 50% 30%, rgba(230,138,0,0.18), transparent 55%)"
          }} />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-20 md:pt-20 md:pb-44">
          <div>
            <div className="font-ui text-[10px] sm:text-xs tracking-[0.4em] text-amber-400/80 uppercase mb-4 sm:mb-6">
              ⚜ Interactive AI Novel · Illustrated · Endlessly Yours ⚜
            </div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-amber-50 leading-[1.05] tracking-tight max-w-4xl">
              The AI Novel Where You<br/>
              <span className="italic text-amber-300">Become the Main Character.</span>
            </h1>

            {/* Always-improving notice — sits right under the headline so
                first-time visitors see "this is iterating fast" before
                they scroll into feature copy. */}
            <div
              data-testid="landing-always-improving"
              className="mt-5 sm:mt-6 max-w-2xl flex items-start gap-2 font-body italic text-amber-200/55 text-xs sm:text-sm leading-relaxed"
            >
              <Sparkles className="w-3.5 h-3.5 text-amber-400/70 mt-0.5 flex-shrink-0" />
              <span>
                We&apos;re always improving — don&apos;t be surprised to see sudden changes or new features as the realms expand.
              </span>
            </div>

            <p className="mt-6 sm:mt-8 max-w-2xl font-body text-base sm:text-lg md:text-xl text-amber-100/75 leading-relaxed">
              TalesTalesTales is an interactive <strong className="text-amber-200">AI Novel</strong> where you create a character,
              enter a <strong className="text-amber-200">story world</strong> and decide what happens next. Light RPG elements such as
              dice rolls, skills, equipment, inventory and objectives add flavour and structure, while the{" "}
              <strong className="text-amber-200">Memory Codex</strong> tracks important characters, events and unresolved story threads.
            </p>
            <p className="mt-4 max-w-2xl font-body text-base sm:text-lg text-amber-200/80 italic">
              Read the story. Make the choices. Become the character.
            </p>
            <p className="mt-3 max-w-2xl font-body text-xs sm:text-sm md:text-base text-amber-100/55 italic">
              <span className="not-italic text-amber-200 font-semibold">Register free → claim 10 turns every day.</span>{" "}
              Switch narrators (Claude, GPT-5, Gemini) · scene illustrations every beat · optional mature storytelling for adults.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to={beginHref}>
                <Button
                  data-testid="hero-cta-begin"
                  size="lg"
                  className="bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui px-8 h-14 text-base candle-glow"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  {user ? "Continue your saga" : "Try 5 free turns"}
                </Button>
              </Link>
              <Link to="/worlds">
                <Button
                  data-testid="hero-cta-explore"
                  variant="outline"
                  size="lg"
                  className="border-amber-500/40 text-amber-200 hover:bg-amber-900/20 hover:text-amber-100 font-ui h-14 px-8 text-base bg-transparent"
                >
                  Explore Worlds
                </Button>
              </Link>
            </div>

            {/* Free Trial banner */}
            <div className="mt-6 max-w-2xl bg-amber-900/10 border border-amber-700/20 rounded-lg p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="flex-1 font-body text-sm text-amber-100/90">
                <strong className="text-amber-200">Free Trial — Try a Premium Narrator</strong>
                <div className="text-amber-100/70">Experience an enhanced narrator with deeper story understanding and richer prose. Start a 2-day free trial of the Mage tier to try premium intelligence and storytelling.</div>
              </div>
              <div className="flex-shrink-0">
                <Link to="/pricing">
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui px-4 h-10">
                    Start Free Trial
                  </Button>
                </Link>
                <Link to="/pricing" className="ml-3">
                  <Button variant="outline" size="sm" className="border-amber-500/40 text-amber-200 hover:bg-amber-900/20 h-10 px-4">
                    Pricing & Details
                  </Button>
                </Link>
              </div>
            </div>

            <section className="mt-5 max-w-2xl border border-amber-700/35 bg-stone-950/55 p-4 sm:p-5" aria-labelledby="narrator-comparison-title">
              <div className="flex flex-col gap-1 border-b border-amber-700/20 pb-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="font-ui text-[10px] uppercase tracking-wider text-amber-400">Same player choice</p>
                  <h2 id="narrator-comparison-title" className="mt-1 font-display text-xl text-amber-50">One scene, two narrator styles</h2>
                </div>
                <p className="font-ui text-xs text-amber-100/55">Illustrative example</p>
              </div>

              <p className="mt-3 font-ui text-xs text-amber-100/70">You choose: Read the sealed letter before the rain reaches the inn.</p>

              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <article className="border border-amber-700/25 bg-stone-900/45 p-4">
                  <p className="font-ui text-[10px] uppercase tracking-wider text-amber-200/65">Free narrator</p>
                  <p className="mt-2 font-body text-sm leading-6 text-amber-100/85">The wax breaks beneath your thumb. The letter warns that the river road will flood by nightfall, and asks you to find the ferryman before he leaves.</p>
                  <div className="mt-3 border-t border-amber-700/15 pt-3">
                    <p className="font-ui text-[10px] uppercase tracking-wider text-amber-200/50">Recommended choices</p>
                    <p className="mt-2 font-ui text-xs text-amber-100/55">Read again · Find the ferryman · Ask the innkeeper</p>
                  </div>
                </article>

                <article className="border border-amber-500/55 bg-amber-950/15 p-4 shadow-[0_0_28px_rgba(245,158,11,0.08)]">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-ui text-[10px] uppercase tracking-wider text-amber-300">Premium narrator</p>
                    <span className="border border-amber-400/35 px-2 py-0.5 font-ui text-[10px] uppercase tracking-wider text-amber-200">Claude</span>
                  </div>
                  <p className="mt-2 font-body text-sm leading-6 text-amber-50">The seal gives with a dry little sigh. Inside, the ferryman repeats the name you heard at the Gilded Anchor and marks the river road in your own charcoal hand: flooded by nightfall. He is not merely leaving. He is waiting for you, and the innkeeper has gone very still.</p>
                  <div className="mt-3 border-t border-amber-500/20 pt-3 font-body text-sm leading-6 text-amber-100/80">
                    <p className="font-ui text-[10px] uppercase tracking-wider text-amber-200/65">Recommended choices</p>
                    <p>Press the innkeeper about why the ferryman used the name from your earlier meeting at the Gilded Anchor.</p>
                    <p className="mt-2">Take the lantern and cut through the rain to reach the river before the road goes under.</p>
                    <p className="mt-2">Read the letter aloud and watch whether the innkeeper reacts to the warning or the name in your hand.</p>
                  </div>
                  <Link to="/pricing" className="mt-4 inline-flex">
                    <Button size="sm" className="bg-amber-500 text-stone-950 hover:bg-amber-400 font-ui">Try Claude free</Button>
                  </Link>
                </article>
              </div>
            </section>

            <div className="mt-12 flex items-center gap-6 text-amber-200/60 font-ui text-sm">
              <span className="flex items-center gap-2"><Crown className="w-4 h-4 text-amber-400" /> 10 free turns / day after register</span>
              <span className="hidden sm:flex items-center gap-2"><Flame className="w-4 h-4 text-amber-400" /> No card required</span>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      {/* THE THREE PILLARS */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-24">
        <div className="ornament mb-12 font-display text-sm">⚜ WHAT MAKES IT A NOVEL, NOT A CHATBOT ⚜</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => (
            <div
              key={f.title}
              data-testid={`feature-card-${i}`}
              className="parchment gold-border rounded-lg p-8 transition-transform hover:-translate-y-1 hover:candle-glow"
            >
              <f.icon className="w-7 h-7 text-amber-400 mb-5" />
              <h3 className="font-display text-2xl text-amber-100 mb-3">{f.title}</h3>
              <p className="font-body text-amber-100/70 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT IS TALESTALESTALES — GEO / AI-search content block. Kept
          semantic (h2/h3, plain prose) so ChatGPT / Perplexity / Google
          AI can extract clear answers about what the product is. */}
      <section
        data-testid="about-block"
        className="relative max-w-4xl mx-auto px-4 sm:px-6 pb-12 sm:pb-16"
        aria-labelledby="about-heading"
      >
        <h2
          id="about-heading"
          className="font-display text-3xl sm:text-4xl text-amber-50 mb-4"
        >
          What is TalesTalesTales?
        </h2>
        <div className="font-body text-amber-100/80 leading-relaxed space-y-4 text-base sm:text-lg">
          <p>
            <strong className="text-amber-200">TalesTalesTales</strong> is an{" "}
            <strong className="text-amber-200">interactive AI Novel</strong>. You create a
            character, enter an illustrated story world, and shape every chapter with your
            choices — dialogue, decisions, the occasional dice roll. An AI narrator writes each
            scene in literary prose, and the{" "}
            <strong className="text-amber-200">Memory Codex</strong> tracks
            important characters, events, inventory changes and unresolved threads to improve continuity.
          </p>
          <p>
            It&apos;s built for <strong className="text-amber-200">readers, solo
            storytellers, worldbuilders, and players curious about light RPG elements</strong>. Switch
            between narrators (Claude Sonnet 4.5, Claude Haiku 4.5, GPT-5, Gemini 2.5
            Pro) mid-book while the Codex carries story details forward. Light RPG elements (dice,
            skills, equipment, inventory) run under the hood if you want them, silent if you don&apos;t.
            Create a character, roll starting stats, collect equipment, track objectives and face occasional dice-based checks while the story remains the focus.
          </p>
          <h3 className="font-display text-xl sm:text-2xl text-amber-100 pt-2">
            Two ways to play
          </h3>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong className="text-amber-200">Novel mode</strong> — the AI narrates in literary prose, dice stay silent, you steer with pure choice</li>
            <li><strong className="text-amber-200">RPG-flavoured mode</strong> — turn on dice, skills, equipment and inventory; the story bends to what the rolls decide</li>
            <li>Or blend them — most sagas end up as illustrated novels with the occasional roll</li>
          </ul>
          <h3 className="font-display text-xl sm:text-2xl text-amber-100 pt-2">
            Core features
          </h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>You are the main character — every chapter is written around you</li>
            <li>Memory Codex — tracks important characters, events, objectives and unresolved threads to improve continuity</li>
            <li>Automatic scene illustrations for every beat</li>
            <li>Character skills, equipment, inventory (with lifecycle history) and an interactive map</li>
            <li>Switchable AI narrators — swap mid-saga while the Codex carries story details forward</li>
            <li>Light RPG elements available when you want them</li>
            <li>Publish your own worlds to the community library</li>
            <li>Optional Mature Realms mode for adult storytelling (18+, opt-in)</li>
          </ul>
          <h3 className="font-display text-xl sm:text-2xl text-amber-100 pt-2">
            Safety and content policy
          </h3>
          <p>
            Standard sagas are safe for all audiences. Mature content is{" "}
            <strong className="text-amber-200">opt-in only</strong>, gated
            behind an 18+ subscription add-on and clear consent prompts.
            Sexual content involving minors, real living people, or
            non-consensual acts is prohibited without exception in every mode.
          </p>
        </div>
      </section>

      {/* FAQ — visible copy mirrors the JSON-LD FAQPage in index.html so
          Google's Rich Results check sees the same answers a real reader
          gets. Kept as native <details> so it works without JS. */}
      <section
        aria-labelledby="landing-faq-heading"
        data-testid="landing-faq"
        className="relative max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16"
      >
        <h2
          id="landing-faq-heading"
          className="font-display text-3xl sm:text-4xl text-amber-50 mb-6 text-center"
        >
          Frequently asked
        </h2>
        <div className="space-y-3">
          {[
            {
              q: "What is TalesTalesTales?",
              a: "TalesTalesTales is an interactive AI Novel. You create a character, enter an illustrated story world, and shape every chapter with your choices. An AI narrator writes each scene in literary prose while the Memory Codex tracks important characters, events, inventory changes, objectives and unresolved threads to improve continuity. Light RPG elements run under the hood if you want them.",
            },
            {
              q: "How is TalesTalesTales different from a chatbot or a book?",
              a: "You are the main character, not a bystander. Every scene is illustrated automatically, your skills and inventory carry across chapters, the Memory Codex feeds earlier story details back to the narrator so chapter 40 can build on chapter 1, and you can switch between AI narrators (Claude Sonnet 4.5, Claude Haiku 4.5, GPT-5, Gemini 2.5 Pro) mid-story.",
            },
            {
              q: "Can I use TalesTalesTales to write an AI novel?",
              a: "Yes. Pick a setting, describe your protagonist, and the AI narrator co-writes a branching illustrated novel with you chapter by chapter. You can keep dice silent for pure novel-writing, or turn them on to let roll outcomes shape the story.",
            },
            {
              q: "How is TalesTalesTales different from AIDungeon, NovelAI, or Fables?",
              a: "TalesTalesTales is an interactive AI Novel, not a chatbot. Every scene is illustrated automatically, the Memory Codex tracks important story details to help long books stay consistent, and you can switch narrators mid-saga. Light RPG elements such as dice, skills, equipment and inventory are optional layers you can turn on or leave off — the story is always the point.",
            },
            {
              q: "Is TalesTalesTales really free?",
              a: "Yes — 10 turns per day on the free Wanderer tier forever, no card. Paid tiers (Apprentice $9.99 / 350 turns/mo, Mage $19.99 / 700, Archmage $39.99 / 1,800) unlock more turns and premium narrators.",
            },
          ].map((item, i) => (
            <details
              key={i}
              data-testid={`landing-faq-${i}`}
              className="parchment gold-border rounded-lg p-4"
            >
              <summary className="font-ui text-sm uppercase tracking-widest text-amber-200 cursor-pointer">
                {item.q}
              </summary>
              <p className="mt-3 font-body text-amber-100/80 leading-relaxed text-sm sm:text-base">
                {item.a}
              </p>
            </details>
          ))}
        </div>
      </section>


      {/* SCROLL EXAMPLE */}
      <section className="relative max-w-6xl mx-auto px-4 sm:px-6 pb-12 sm:pb-24">
        <div className="grid md:grid-cols-2 gap-6 sm:gap-10 items-center">
          <div>
            <div className="font-ui text-xs tracking-[0.35em] text-amber-400/80 mb-4">A GLIMPSE</div>
            <h2 className="font-display text-4xl md:text-5xl text-amber-50 leading-tight mb-6">
              Every turn, an illumination.
            </h2>
            <p className="font-body text-lg text-amber-100/75 leading-relaxed mb-6">
              Speak your intent. Roll the bones. Watch the narrator breathe life into
              every flicker of torchlight, every whispered prophecy, every clash of steel.
            </p>
            <p className="font-body italic text-amber-300/70">
              "And then, with a single trembling hand, you push open the oaken door..."
            </p>
          </div>

          <div className="relative rounded-xl overflow-hidden gold-border parchment p-8">
            <img
              src={PARCHMENT}
              alt=""
              loading="lazy"
              decoding="async"
              width="940"
              height="650"
              className="absolute inset-0 w-full h-full object-cover opacity-20 mix-blend-overlay"
            />
            <div className="relative">
              <div className="font-ui text-[10px] tracking-widest text-amber-500/80 uppercase mb-2">Narrator</div>
              <p className="font-body text-amber-50/90 leading-relaxed">
                The tavern's hearth crackles low as the hooded stranger slides a folded
                parchment across the worn oak. <span className="gold-text italic">"Take it,"</span> they
                rasp, eyes glimmering beneath the cowl, <span className="gold-text italic">"and tell no one
                of the marsh-witch."</span>
              </p>
              <div className="mt-5 pt-5 border-t border-amber-700/20 font-body italic text-amber-200/70 text-sm">
                <em>You may: read the parchment · question the stranger · slip out into the rain</em>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative">
        <div className="absolute inset-0 opacity-30">
          <img src={HERO_BG_JPEG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f0d11] via-[#0f0d11]/80 to-[#0f0d11]/60" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-28 text-center">
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl text-amber-50 mb-6">
            The road is dark.<br/>
            <span className="italic text-amber-300">Your lantern is lit.</span>
          </h2>
          <p className="font-body text-lg text-amber-100/70 mb-10 max-w-xl mx-auto">
            Gather your courage. Your first chapter is a click away.
          </p>
          <Link to={forgeHref}>
            <Button data-testid="cta-final-btn" size="lg" className="bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui h-14 px-10 candle-glow">
              <Flame className="w-5 h-5 mr-2" /> Forge Your Hero
            </Button>
          </Link>
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
}
